/* eslint-disable @next/next/no-img-element */
import { ElementProps } from '@/components/sections/builder/types';

import {
  TextWithIconColumnType,
  TextWithIconElementDataType,
} from '@/components/sections/builder/elements/body/text-with-icon/type';
import ImageSetting from '@/components/sections/builder/settings/image-setting';

import { useBuilderStore } from '@/stores/builder';
import clsx from 'clsx';
import React from 'react';
import { shallow } from 'zustand/shallow';
import { Columns } from 'lucide-react';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';

const defaultData: TextWithIconColumnType = {
  name: 'Column',
  heading: 'Harga Khusus',
  description: 'Dapatkan harga khusus dengan pembelian jumlah besar',
  imageUrl: '',
  colorScheme: DEFAULT_THEME_COLOR_SCHEME,
};

export const ElementContent = ({ element }: ElementProps) => {
  const data = (element.data || defaultData) as TextWithIconColumnType;
  const parentData = element.parentData as TextWithIconElementDataType;

  return (
    <>
      <div
        className={clsx(
          'border_radius border_style flex h-full p-4',
          parentData.imagePosition === 'left' && 'items-center',
          parentData.imagePosition === 'right' &&
            'items-center md:flex-row-reverse lg:flex-row-reverse',
          parentData.imagePosition === 'top' && 'flex-col items-center',
          parentData.imagePosition === 'top-left' && 'flex-col items-start',
          parentData.imagePosition === 'top-right' && 'flex-col items-end',
          parentData.imagePosition === 'bottom' && 'flex-col-reverse items-center',
          parentData.imagePosition === 'bottom-left' && 'flex-col-reverse items-start',
          parentData.imagePosition === 'bottom-right' && 'flex-col-reverse items-end'
        )}
        style={{
          backgroundColor: data.colorScheme?.background || '#FFFFFF',
        }}
        data-animation='true'
        data-animation-group={`texticon-item-${element.parentId}`}
      >
        {data.imageUrl && (
          <img
            src={data.imageUrl}
            alt='content'
            className={clsx(
              'relative',
              parentData.imageWidth === 'small' && 'h-[50px] w-[50px]',
              parentData.imageWidth === 'medium' && 'h-[80px] w-[80px]',
              parentData.imageWidth === 'large' && 'h-[120px] w-[120px]'
            )}
          />
        )}
        <div
          className={clsx(
            'pt-4',
            parentData.columnAlignment === 'center'
              ? 'text-left md:text-center lg:text-center'
              : 'text-left'
          )}
        >
          <p
            className='mb-3 text-lg tracking-tight'
            style={{
              color: data.colorScheme?.text || '#52535B',
            }}
            dangerouslySetInnerHTML={{ __html: data.heading }}
          />
          <p
            className='text-sm !font-light'
            dangerouslySetInnerHTML={{ __html: data.description }}
            style={{
              color: data.colorScheme?.textSecondary || '#52535B',
              fontWeight: 'light',
            }}
          />
        </div>
      </div>
      <style jsx>
        {`
          .border_style {
            border: 2px ${parentData.borderStyle} ${parentData.colorScheme?.text || '#52535B'};
          }
          .border_radius {
            border-radius: ${parentData.borderRadius}px;
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Columns;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as TextWithIconColumnType;

  return (
    <div>
      <ImageSetting
        value={data?.imageUrl}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            imageUrl: v,
          })
        }
      />
      <div className='mb-1 text-sm md:mt-4'>Heading</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            heading: value,
          })
        }
        value={data.heading}
        className='mb-4 w-full max-w-64'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-1 text-sm md:mt-4'>Description</div>
      <TextEditorMinimal
        onChange={(value) =>
          builder.setChildData(element.parentId as string, element.id, {
            description: value,
          })
        }
        value={data.description}
        className='mb-4 w-full'
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setChildData(element.parentId as string, element.id, {
            colorScheme: theme,
          });
        }}
      />
    </div>
  );
}

const Column = {
  type: 'column',
  group: 'text_with_icon',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Column;
